import { useEffect, useState, useMemo } from 'react';
import { useJobStore } from '../stores/jobStore';
import { useTranslation } from 'react-i18next';
import { JobPriority, ListingType } from '../types/job';
import { mapListingTypesToPriorities } from '../utils/priorityUtils';
import { useCreditStore } from '../stores/creditStore';

const getSelectedOptions = (
  options: JobPriority[],
  profileListingType?: ListingType | null
): JobPriority | null => {
  if (!profileListingType) return null;
  return options.find(opt => opt.id === profileListingType.id) || null;
};

export const useListingTypeSelector = (profileListingType?: ListingType | null) => {
  const { t } = useTranslation('jobs');
  const { listingTypes, loadingListingTypes, hasSettledListingTypesLoad, fetchListingTypes } =
    useJobStore();
  const { actionCosts, fetchActionCosts } = useCreditStore();

  const options = useMemo(
    () => mapListingTypesToPriorities(listingTypes, t, actionCosts),
    [listingTypes, t, actionCosts]
  );

  const [selectedOption, setSelectedOption] = useState<JobPriority | null>(
    getSelectedOptions(options, profileListingType)
  );

  useEffect(() => {
    if (!listingTypes.length) {
      fetchListingTypes();
    }
    if (!Object.keys(actionCosts).length) {
      fetchActionCosts();
    }
  }, [listingTypes.length, actionCosts, fetchActionCosts, fetchListingTypes]);

  useEffect(() => {
    if (!selectedOption) {
      const initialSelectedOption = getSelectedOptions(options, profileListingType);
      if (initialSelectedOption) {
        setSelectedOption(initialSelectedOption);
      }
    }
  }, [profileListingType, options, selectedOption]);

  return {
    options,
    selectedOption,
    setSelectedOption,
    loading: loadingListingTypes,
    hasSettledOptionsLoad: hasSettledListingTypesLoad || listingTypes.length > 0,
  };
};
